import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useForm } from "../../../../hooks";
import { PrincipalLayouts } from "../../../../layouts";
import { NumberInput } from "../../NumberInput";
import { fetchData } from "../../../helpers/getData/fetchData";
import { createInvestmentFund } from "../../../helpers/investmentFunds";
import { DateInput } from "../../DateInput";

export const AddContributions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [isWaiting, setIsWaiting] = useState(false);
  const [userList, setUserList] = useState("isLoading");
  const [companiesList, setCompaniesList] = useState("isLoading");

  const [formValues, handleInputChange, reset] = useForm({
    name: "",
    amountInverter: "",
    whoInvest: "",
    investmentDate: "",
  });

  const { name, amountInverter, whoInvest, investmentDate } = formValues;

  const handleConfirm = async (e) => {
    e.preventDefault();
    setIsWaiting(true);
    createInvestmentFund(
      {
        whoInvest,
        amount: amountInverter,
        individualId: userList.filter((user) => user.name === name)[0]?.id,
        fundId: searchParams.get("fund"),
        investmentDate
      },
      t("contributions.succesMsg"),
      t("contributions.errorMsg"),
      navigate
    );
    setIsWaiting(false);
    reset();
  };

  const handleCancel = () => {
    reset();
    navigate(-1);
  };

  useEffect(() => {
    fetchData(setUserList, "/user/getListNameAndId?type=LP", t);
  }, [t]);

  useEffect(() => {
    if (userList !== "isLoading") {
      const id = userList.filter((user) => user.name === name)[0]?.id;
      fetchData(setCompaniesList, `/companies/listNameQuery?name=${id}`, t);
      handleInputChange({ target: { name: "whoInvest", value: name } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, name, userList]);

  return (
    <PrincipalLayouts>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography color="primary" variant="h5">
          {t("funds.addContributions")}
        </Typography>
      </Stack>
      <Divider sx={{ marginTop: 2, marginBottom: 3 }} />

      <form onSubmit={handleConfirm}>
        <Stack minHeight="calc(100vh - 290px)" gap={2}>
          {userList !== "isLoading" ? (
            <Stack direction="column" width="100%" gap={0}>
              <Typography variant="overline" color="secondary">
                {t("funds.partner")}
              </Typography>
              <Autocomplete
                disablePortal
                options={[...userList.map((user) => user.name), ""]}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" name="name" />
                )}
                value={name}
                onSelect={handleInputChange}
                fullWidth
                required
              />
            </Stack>
          ) : (
            <Stack width="100%" alignItems="center" marginTop="100px">
              <CircularProgress />
            </Stack>
          )}
          {name !== "" && companiesList !== "isLoading" && (
            <>
              <Stack direction="column" width="100%" gap={0}>
                <Typography variant="overline" color="secondary">
                  {t("funds.whoInvest")}
                </Typography>
                <Autocomplete
                  disablePortal
                  options={[...companiesList, name]}
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      name="whoInvest"
                    />
                  )}
                  value={whoInvest}
                  onSelect={handleInputChange}
                  fullWidth
                  required
                />
              </Stack>

              <Stack direction="column" width="100%" gap={0}>
                <NumberInput
                  name={amountInverter}
                  title="amountInverter"
                  handleInputChange={handleInputChange}
                  required={true}
                  min={10}
                />
              </Stack>

              <Stack direction="column" width="100%" gap={0}>
                <DateInput
                  name={investmentDate}
                  title="investmentDate"
                  handleInputChange={handleInputChange}
                  translation="investmentDate"
                  required={true}
                />
              </Stack>
            </>
          )}
        </Stack>

        <Stack direction="row" justifyContent="end" gap={2} marginTop={3}>
          <Button
            sx={{ width: "140px" }}
            color="secondary"
            onClick={handleCancel}
            variant="outlined"
          >
            {t("myprofile.cancelButton")}
          </Button>
          <Button
            disabled={isWaiting}
            sx={{ width: "140px" }}
            type="submit"
            variant="contained"
          >
            {t("myprofile.confirmButton")}
          </Button>
        </Stack>
      </form>
    </PrincipalLayouts>
  );
};
